// src/pages/letters/LetterForm.jsx
import React from 'react';
import JalaliDatePicker from '../../components/JalaliDatePicker';
import RichTextEditor from '../../components/RichTextEditor';
import AttachmentsUploader from './AttachmentsUploader';
import { getTodayJalali } from '../../utils/factorHelpers';
import './Letters.css';
import api from '../../core/api';
const MAX_COPIES = 10;

export const emptyCopy = () => ({ name: '', position: '' });

export const emptyForm = () => ({
  date: getTodayJalali(),
  recipientName: '',
  recipientPosition: '',
  signerName: '',
  signerPosition: '',
  subject: '',
  body: '',
  hasCopies: false,
  copies: [emptyCopy()],
  attachments: [],
});


export async function discardUnsavedAttachments(current, saved = []) {
  const fresh = (current || []).filter((p) => !saved.includes(p));
  await Promise.allSettled(
    fresh.map((path) => api.post('/letters/attachment/delete', { path }))
  );
}

/* فرم → بدنه‌ی درخواست API */
export function buildPayload(form) {
  return {
    date: form.date || getTodayJalali(),
    recipientName: form.recipientName.trim(),
    recipientPosition: form.recipientPosition.trim(),
    signerName: form.signerName.trim(),
    signerPosition: form.signerPosition.trim(),
    subject: form.subject.trim(),
    body: form.body,
    copies: form.hasCopies
      ? form.copies
          .map((c) => ({ name: c.name.trim(), position: c.position.trim() }))
          .filter((c) => c.name)
      : [],
    attachments: form.attachments || [],
  };
}

/* خروجی: متن خطا یا رشته‌ی خالی */
export function validateForm(form) {
  if (!form.subject.trim()) return 'موضوع نامه الزامی است';
  if (form.hasCopies && !form.copies.some((c) => c.name.trim())) {
    return 'نام رونوشت را وارد کنید یا تیک رونوشت را بردارید';
  }
  return '';
}

export default function LetterForm({ form, setForm, serial, onDirty, onMessage, onUploadingChange }) {

  const set = (patch) => {
    setForm((prev) => ({ ...prev, ...patch }));
    onDirty?.();
  };

  const toggleCopies = (checked) => {
    setForm((prev) => ({
      ...prev,
      hasCopies: checked,
      copies: prev.copies.length ? prev.copies : [emptyCopy()],
    }));
    onDirty?.();
  };

  const updateCopy = (i, patch) => {
    setForm((prev) => ({
      ...prev,
      copies: prev.copies.map((c, idx) => (idx === i ? { ...c, ...patch } : c)),
    }));
    onDirty?.();
  };

  const addCopy = () => {
    setForm((prev) =>
      prev.copies.length >= MAX_COPIES
        ? prev
        : { ...prev, copies: [...prev.copies, emptyCopy()] }
    );
    onDirty?.();
  };

  const removeCopy = (i) => {
    setForm((prev) =>
      prev.copies.length <= 1
        ? prev
        : { ...prev, copies: prev.copies.filter((_, idx) => idx !== i) }
    );
    onDirty?.();
  };

  const changeAttachments = (updater) => {
    setForm((prev) => ({ ...prev, attachments: updater(prev.attachments || []) }));
    onDirty?.();
  };

  return (
    <>
      {/* ============ اطلاعات نامه ============ */}
      <div className="card">
        <h2>اطلاعات نامه</h2>
        <div className="grid">
          <div>
            <label>شماره نامه</label>
            <input
              value={serial || ''}
              readOnly
              disabled
              placeholder="پس از ثبت، خودکار تعیین می‌شود"
              style={{ opacity: 0.7, cursor: 'not-allowed' }}
            />
          </div>

          <div>
            <label>تاریخ نامه</label>
            <JalaliDatePicker
              value={form.date}
              onChange={(v) => set({ date: v })}
              placeholder="انتخاب تاریخ نامه"
              name="date"
            />
          </div>

          <div>
            <label>نام گیرنده</label>
            <input
              autoComplete="off"
              value={form.recipientName}
              onChange={(e) => set({ recipientName: e.target.value })}
            />
          </div>

          <div>
            <label>سمت گیرنده</label>
            <input
              autoComplete="off"
              value={form.recipientPosition}
              onChange={(e) => set({ recipientPosition: e.target.value })}
            />
          </div>

          <div>
            <label>نام امضا کننده</label>
            <input
              autoComplete="off"
              value={form.signerName}
              onChange={(e) => set({ signerName: e.target.value })}
            />
          </div>

          <div>
            <label>سمت امضا کننده</label>
            <input
              autoComplete="off"
              value={form.signerPosition}
              onChange={(e) => set({ signerPosition: e.target.value })}
            />
          </div>

          <div className="field-full">
            <label>موضوع نامه *</label>
            <input
              required
              autoComplete="off"
              value={form.subject}
              onChange={(e) => set({ subject: e.target.value })}
              placeholder="موضوع در لیست نامه‌ها نمایش داده می‌شود"
            />
          </div>
        </div>
      </div>

      {/* ============ متن نامه ============ */}
      <div className="card">
        <h2>متن نامه</h2>
        <RichTextEditor value={form.body} onChange={(html) => set({ body: html })} />
      </div>

      {/* ============ پیوست ============ */}
      <div className="card">
        <h2>پیوست</h2>
        <AttachmentsUploader
    value={form.attachments || []}
    onChange={changeAttachments}
    onMessage={onMessage}
    onUploadingChange={onUploadingChange}
  />
      </div>

      {/* ============ رونوشت ============ */}
      <div className="card">
        <h2>رونوشت</h2>

        <label className="lt-check">
          <input
            type="checkbox"
            checked={form.hasCopies}
            onChange={(e) => toggleCopies(e.target.checked)}
          />
          <span>این نامه رونوشت دارد</span>
        </label>

        {form.hasCopies && (
          <div className="lt-copies">
            {form.copies.map((c, i) => (
              <div className="lt-copy-row" key={i}>
                <span className="lt-copy-n">{(i + 1).toLocaleString('fa-IR')}</span>
                <input
                  autoComplete="off"
                  placeholder="نام"
                  value={c.name}
                  onChange={(e) => updateCopy(i, { name: e.target.value })}
                />
                <input
                  autoComplete="off"
                  placeholder="سمت"
                  value={c.position}
                  onChange={(e) => updateCopy(i, { position: e.target.value })}
                />
                <button
                  type="button"
                  className="btn danger small"
                  onClick={() => removeCopy(i)}
                  disabled={form.copies.length <= 1}
                  aria-label="حذف رونوشت"
                >
                  <i className="fa-solid fa-xmark" />
                </button>
              </div>
            ))}

            <div className="lt-copies-add">
              <button
                type="button"
                className="btn secondary small"
                onClick={addCopy}
                disabled={form.copies.length >= MAX_COPIES}
              >
                <i className="fa-solid fa-plus" /> رونوشت دیگر
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}